import type { ComponentProps, ReactNode } from "react";
import type {
	BadgeColors,
	BadgeTypes,
	IconComponentType,
} from "@/components/base/badges/badge-types";
import { EmptyValue } from "@/components/common";
import { useAppCellContextBase } from "./app-table-contexts";
import { TableBodyCell, type TableBodyCellAlign } from "./table-body-cell";
import { TableBodyRow } from "./table-body-row";
import { TableCellContent, TableCellContentType } from "./table-cell-content";
import {
	TableHeaderCell,
	type TableHeaderCellAlign,
	type TableHeaderCellSortable,
	type TableHeaderCellState,
} from "./table-header-cell";
import { TableHeaderRow } from "./table-header-row";
import {
	TableSelectionCellHeader,
	type TableSelectionCellHeaderState,
} from "./table-selection-cell-header";
import {
	TableSelectionCellRow,
	type TableSelectionCellRowState,
} from "./table-selection-cell-row";

function valueAsNode(value: unknown): ReactNode {
	if (value == null) return null;
	if (
		typeof value === "string" ||
		typeof value === "number" ||
		typeof value === "boolean"
	) {
		return String(value);
	}
	return value as ReactNode;
}

/** True when `getValue()` should render {@link EmptyValue} instead of text. */
function isEmptyCellValue(value: unknown): boolean {
	if (value == null) return true;
	if (typeof value === "string") return value.trim().length === 0;
	return false;
}

/**
 * Destiny {@link TableBodyCell} shell for composing cell content outside React Aria.
 * Prefer `CustomTable.Cell` for live Aria tables (same Figma BodyCell chrome).
 *
 * Must receive `children` — do not use as a FlexRender target (would recurse).
 *
 * @example
 * ```tsx
 * <table.AppCell cell={cell}>
 *   {(c) => (
 *     <c.BodyCell>
 *       <c.SecondaryTextCell />
 *     </c.BodyCell>
 *   )}
 * </table.AppCell>
 * ```
 */
export function BodyCell({
	align,
	children,
	className,
}: {
	align?: TableBodyCellAlign;
	children: ReactNode;
	className?: string;
}) {
	return (
		<TableBodyCell
			align={align}
			className={className ?? "h-full min-w-0 w-auto max-w-none"}
		>
			{children}
		</TableBodyCell>
	);
}

/** Secondary (default) text cell — Figma `type=secondary text only`. */
export function SecondaryTextCell() {
	const cell = useAppCellContextBase();
	return (
		<TableCellContent
			type={TableCellContentType.Secondary}
			primary={valueAsNode(cell.getValue())}
		/>
	);
}

/**
 * Secondary text, or {@link EmptyValue} when the cell value is null/blank.
 * Prefer formatted accessors (e.g. `# 123`) so `getValue()` is display-ready.
 */
export function SecondaryTextOrEmptyCell() {
	const cell = useAppCellContextBase();
	const value = cell.getValue();
	if (isEmptyCellValue(value)) return <EmptyValue />;
	return (
		<TableCellContent
			type={TableCellContentType.Secondary}
			primary={valueAsNode(value)}
		/>
	);
}

/** Primary (extrabold) text cell — Figma `type=primary text only`. */
export function PrimaryTextCell() {
	const cell = useAppCellContextBase();
	return (
		<TableCellContent
			type={TableCellContentType.Primary}
			primary={valueAsNode(cell.getValue())}
		/>
	);
}

/**
 * Primary text, or {@link EmptyValue} when the cell value is null/blank.
 */
export function PrimaryTextOrEmptyCell() {
	const cell = useAppCellContextBase();
	const value = cell.getValue();
	if (isEmptyCellValue(value)) return <EmptyValue />;
	return (
		<TableCellContent
			type={TableCellContentType.Primary}
			primary={valueAsNode(value)}
		/>
	);
}

/** Two-line primary + secondary — Figma `type=primary and secondary text`. */
export function PrimaryAndSecondaryCell({
	secondary,
}: {
	secondary?: ReactNode;
}) {
	const cell = useAppCellContextBase();
	return (
		<TableCellContent
			type={TableCellContentType.PrimaryAndSecondary}
			primary={valueAsNode(cell.getValue())}
			secondary={secondary}
		/>
	);
}

/** Date cell — Figma `type=date`. */
export function DateCell() {
	const cell = useAppCellContextBase();
	return (
		<TableCellContent
			type={TableCellContentType.Date}
			primary={valueAsNode(cell.getValue())}
		/>
	);
}

/**
 * Date cell, or {@link EmptyValue} when the value is null/blank.
 * Pass a pre-formatted display string via the column accessor.
 */
export function DateOrEmptyCell() {
	const cell = useAppCellContextBase();
	const value = cell.getValue();
	if (isEmptyCellValue(value)) return <EmptyValue />;
	return (
		<TableCellContent
			type={TableCellContentType.Date}
			primary={valueAsNode(value)}
		/>
	);
}

/** Date + time cell — Figma `type=date and time`. */
export function DateAndTimeCell() {
	const cell = useAppCellContextBase();
	return (
		<TableCellContent
			type={TableCellContentType.DateAndTime}
			primary={valueAsNode(cell.getValue())}
		/>
	);
}

/** Phone cell — Figma `type=phone`. */
export function PhoneCell() {
	const cell = useAppCellContextBase();
	return (
		<TableCellContent
			type={TableCellContentType.Phone}
			primary={valueAsNode(cell.getValue())}
		/>
	);
}

/** Badge cell — Figma `type=badge` (dot by default; pass `type="pill-color"` for pill). */
export function BadgeCell({
	color = "gray",
	type = "modern",
	icon,
}: {
	color?: BadgeColors;
	/** Badge shape: `pill-color` | `color` | `modern`. */
	type?: BadgeTypes;
	/** When set, renders BadgeWithIcon instead of BadgeWithDot. */
	icon?: IconComponentType;
}) {
	const cell = useAppCellContextBase();
	return (
		<TableCellContent
			type={TableCellContentType.Badge}
			primary={valueAsNode(cell.getValue())}
			badgeColor={color}
			badgeType={type}
			icon={icon}
		/>
	);
}

/**
 * Badge cell, or {@link EmptyValue} when the value is null/blank.
 * Pass per-row `color` / `type` / `icon` from the column `cell` renderer when needed.
 */
export function BadgeOrEmptyCell({
	color = "gray",
	type = "pill-color",
	icon,
}: {
	color?: BadgeColors;
	/** Badge shape: `pill-color` | `color` | `modern`. Defaults to pill. */
	type?: BadgeTypes;
	/** When set, renders BadgeWithIcon instead of BadgeWithDot. */
	icon?: IconComponentType;
}) {
	const cell = useAppCellContextBase();
	const value = cell.getValue();
	if (isEmptyCellValue(value)) return <EmptyValue />;
	return (
		<TableCellContent
			type={TableCellContentType.Badge}
			primary={valueAsNode(value)}
			badgeColor={color}
			badgeType={type}
			icon={icon}
		/>
	);
}

/** Label cell — Figma `type=label`. */
export function LabelCell({ icon }: { icon?: IconComponentType }) {
	const cell = useAppCellContextBase();
	return (
		<TableCellContent
			type={TableCellContentType.Label}
			primary={valueAsNode(cell.getValue())}
			icon={icon}
		/>
	);
}

/**
 * Label cell, or {@link EmptyValue} when the value is null/blank.
 * Pass per-row `icon` / `primary` from the column `cell` renderer when needed
 * (e.g. resolve a display name that differs from the accessor value).
 */
export function LabelOrEmptyCell({
	icon,
	primary,
}: {
	icon?: IconComponentType;
	/** Override `getValue()` — use when the accessor is an id but the label needs a name. */
	primary?: ReactNode;
}) {
	const cell = useAppCellContextBase();
	const value = primary !== undefined ? primary : cell.getValue();
	if (isEmptyCellValue(value)) return <EmptyValue />;
	return (
		<TableCellContent
			type={TableCellContentType.Label}
			primary={valueAsNode(value)}
			icon={icon}
		/>
	);
}

/** Featured-icon cell — Figma `type=featured icon`. */
export function FeaturedIconCell({ icon }: { icon: IconComponentType }) {
	return (
		<TableCellContent type={TableCellContentType.FeaturedIcon} icon={icon} />
	);
}

/** Address cell — Figma `type=address`. */
export function AddressCell({ secondary }: { secondary?: ReactNode }) {
	const cell = useAppCellContextBase();
	return (
		<TableCellContent
			type={TableCellContentType.Address}
			primary={valueAsNode(cell.getValue())}
			secondary={secondary}
		/>
	);
}

/** Copyable id cell — Figma `type=copyable Id`. */
export function CopyableIdCell({ onCopy }: { onCopy?: () => void }) {
	const cell = useAppCellContextBase();
	return (
		<TableCellContent
			type={TableCellContentType.CopyableId}
			primary={valueAsNode(cell.getValue())}
			onCopy={onCopy}
		/>
	);
}

/** Avatar + label cell — Figma `type=avatar label`. */
export function AvatarLabelCell({
	secondary,
	avatarSrc,
	avatarAlt,
}: {
	secondary?: ReactNode;
	avatarSrc?: string;
	avatarAlt?: string;
}) {
	const cell = useAppCellContextBase();
	return (
		<TableCellContent
			type={TableCellContentType.AvatarLabel}
			primary={valueAsNode(cell.getValue())}
			secondary={secondary}
			avatarSrc={avatarSrc}
			avatarAlt={avatarAlt}
		/>
	);
}

/** Empty / dash cell — Figma `type=empty`. */
export function EmptyCell() {
	return <TableCellContent type={TableCellContentType.Empty} />;
}

/**
 * Destiny {@link TableHeaderRow} for Storybook / non-Aria composition.
 * Live React Aria tables use `CustomTable.Header` for the same chrome.
 */
export function HeaderRow(props: ComponentProps<typeof TableHeaderRow>) {
	return <TableHeaderRow {...props} />;
}

/**
 * Destiny {@link TableBodyRow} for Storybook / non-Aria composition.
 * Live React Aria tables use `CustomTable.Row` for the same chrome.
 */
export function BodyRow(props: ComponentProps<typeof TableBodyRow>) {
	return <TableBodyRow {...props} />;
}

/**
 * Destiny {@link TableHeaderCell} shell for composing headers outside React Aria.
 * Prefer `CustomTable.Head` for live Aria tables (same Figma HeaderCell chrome).
 */
export function HeaderCell({
	align,
	sortable,
	state,
	label,
	overline,
	hasOverline,
	hasTooltip,
	tooltip,
	hasResizer,
	hasDragHandle,
	children,
	className,
}: {
	align?: TableHeaderCellAlign;
	sortable?: TableHeaderCellSortable;
	state?: TableHeaderCellState;
	label?: string;
	overline?: string;
	hasOverline?: boolean;
	hasTooltip?: boolean;
	tooltip?: string;
	hasResizer?: boolean;
	hasDragHandle?: boolean;
	children?: ReactNode;
	className?: string;
}) {
	return (
		<TableHeaderCell
			align={align}
			sortable={sortable}
			state={state}
			label={label}
			overline={overline}
			hasOverline={hasOverline}
			hasTooltip={hasTooltip}
			tooltip={tooltip}
			hasResizer={hasResizer}
			hasDragHandle={hasDragHandle}
			className={className ?? "h-full min-w-0 w-auto max-w-none"}
		>
			{children}
		</TableHeaderCell>
	);
}

/**
 * Presentational selection header chrome for Storybook / non-Aria demos.
 * Live tables compose {@link TableSelectionCellHeader} around React Aria
 * `Checkbox slot="selection"` inside `CustomTable.Header`.
 */
export function SelectionHeaderCell({
	state,
}: {
	state?: TableSelectionCellHeaderState;
}) {
	return <TableSelectionCellHeader state={state} />;
}

/**
 * Presentational selection row chrome for Storybook / non-Aria demos.
 * Live tables compose {@link TableSelectionCellRow} around React Aria
 * `Checkbox slot="selection"` inside `CustomTable.Row`.
 */
export function SelectionRowCell({
	state,
}: {
	state?: TableSelectionCellRowState;
}) {
	return <TableSelectionCellRow state={state} />;
}

/** Registered on `createTableHook` — available as `cell.SecondaryTextCell` via `AppCell`. */
export const appTableCellComponents = {
	BodyCell,
	SecondaryTextCell,
	SecondaryTextOrEmptyCell,
	PrimaryTextCell,
	PrimaryTextOrEmptyCell,
	PrimaryAndSecondaryCell,
	DateCell,
	DateOrEmptyCell,
	DateAndTimeCell,
	PhoneCell,
	BadgeCell,
	BadgeOrEmptyCell,
	LabelCell,
	LabelOrEmptyCell,
	FeaturedIconCell,
	AddressCell,
	CopyableIdCell,
	AvatarLabelCell,
	EmptyCell,
	SelectionRowCell,
};

/** Registered on `createTableHook` — available as `header.SelectionHeaderCell` via `AppHeader`. */
export const appTableHeaderComponents = {
	HeaderCell,
	HeaderRow,
	SelectionHeaderCell,
};

/**
 * Table-level Destiny shells — available on the table instance from `useAppTable`.
 * Live Aria tables still render via `CustomTable.Header` / `CustomTable.Row`.
 */
export const appTableShellComponents = {
	HeaderRow,
	BodyRow,
};

export type AppTableCellComponents = typeof appTableCellComponents;
export type AppTableHeaderComponents = typeof appTableHeaderComponents;
export type AppTableShellComponents = typeof appTableShellComponents;
